import { sanitizeHtml } from './sanitizeHtml';
import { getTokenByKey } from '../components/template/tokenCatalog';
import { applyFormatter } from '../components/template/formatters';

/** Format number as USD for display (single source for print/PDF/email to avoid clipping bugs). Exported for tests. */
export function formatCurrency(value) {
  const n = typeof value === 'number' ? value : parseFloat(String(value ?? 0).replace(/[$,]/g, '')) || 0;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

/** Parse value to number, handling currency strings like "$1,250.00". */
function parseCurrencyToNumber(value) {
  if (value == null) return 0;
  const n = typeof value === 'number' ? value : parseFloat(String(value).replace(/[$,]/g, '')) || 0;
  return Number.isNaN(n) ? 0 : n;
}

function escapeHtml(text) {
  if (text == null) return '';
  const s = String(text);
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * UNIFIED TEMPLATE RENDERER
 *
 * This is the SINGLE SOURCE OF TRUTH for rendering templates.
 * All PDF, Email, Print, and Preview outputs MUST use this function.
 * Output is sanitized before return to prevent XSS.
 *
 * @param {string} html - Template HTML with merge tokens
 * @param {object} data - Data object containing invoice/client information
 * @param {boolean} hideTotals - Whether to hide totals section (optional, default: false)
 * @returns {string} - Rendered, sanitized HTML with all tokens replaced (styling preserved via span wrappers)
 */
export function renderTemplate(html, data, hideTotals = false) {
  if (!html) return '';

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  
  // Prepare items array (handle both string and array formats)
  let items = Array.isArray(data.invoice?.line_items) 
    ? data.invoice.line_items 
    : Array.isArray(data.invoice?.items_table)
    ? data.invoice.items_table
    : typeof data.invoice?.line_items === 'string'
    ? JSON.parse(data.invoice.line_items)
    : typeof data.invoice?.items_table === 'string'
    ? JSON.parse(data.invoice.items_table)
    : [];

  // Normalize item keys so both unit_price/unitPrice and total work (API/DB may use either)
  items = items.map((item) => {
    if (!item || typeof item !== 'object') return item;
    const qty = parseFloat(item.quantity ?? item.qty ?? 0) || 0;
    let numUnitPrice = parseFloat(item.unit_price ?? item.unitPrice);
    let total = item.total !== undefined && item.total !== null && item.total !== '' ? parseFloat(item.total) : NaN;
    if (Number.isNaN(total)) total = qty * (Number.isNaN(numUnitPrice) ? 0 : numUnitPrice);
    if ((Number.isNaN(numUnitPrice) || numUnitPrice === 0) && qty > 0 && total > 0) numUnitPrice = total / qty;
    return {
      ...item,
      description: item.description ?? item.Description ?? '',
      quantity: qty,
      unit_price: Number.isNaN(numUnitPrice) ? 0 : numUnitPrice,
      taxable: item.taxable !== undefined ? item.taxable : true,
      total: Number.isNaN(total) ? 0 : total,
    };
  });

  // Data rows only (exclude separators) - used so per-row tokens like invoice.items_table.1.unit_price
  // refer to the 2nd line item, not the 2nd array element (which might be a separator).
  // Also exclude separator-like rows when isSeparator isn't set (e.g. after load from API).
  function isSeparatorRow(item) {
    if (item?.isSeparator === true) return true;
    const desc = String(item?.description ?? '').trim();
    const qty = item?.quantity ?? 0;
    const price = parseFloat(item?.unit_price || 0);
    const total = parseFloat(item?.total || 0);
    return desc === '' && qty === 0 && price === 0 && total === 0;
  }
  const dataRows = items.filter((i) => !isSeparatorRow(i));
  
  // Find all merge tokens by data-key attribute (new system)
  const tokens = doc.querySelectorAll('.merge-token[data-key]');
  
  tokens.forEach(token => {
    const key = token.getAttribute('data-key');
    if (!key) return;
    
    // Special handling for complex sections
    if (key === 'invoice.line_items' || key === 'invoice.items_table' || key === 'estimate.line_items' || key === 'estimate.items_table') {
      // Generate items table HTML (include separator rows so order and styling are correct).
      // colgroup + money-cell prevent column collapse after separator row (fixes $ + first digit clipping).
      // Add compact class when table could overflow so estimate/invoice fits on single page.
      const dataRowCount = dataRows.length;
      let totalTableRows = 1; // header
      let prevSectionTotal = 0;
      items.forEach((item) => {
        if (isSeparatorRow(item)) {
          if (item.showSubtotal && prevSectionTotal > 0) totalTableRows++;
          totalTableRows++;
          prevSectionTotal = 0;
        } else {
          totalTableRows++;
          prevSectionTotal += parseFloat(item.total) || 0;
        }
      });
      const needsXCompact = totalTableRows > 15 || dataRowCount > 10;
      const needsCompact = !needsXCompact && (totalTableRows > 8 || dataRowCount > 5);
      const tableClass = needsXCompact ? 'print-items-table print-items-table--xcompact' : needsCompact ? 'print-items-table print-items-table--compact' : 'print-items-table';
      const taxRate = parseFloat(data.invoice?.tax_rate || data.tax_rate || 0);
      const includeTax = data.invoice?.include_tax !== false;
      let itemsTableHtml = `<table class="${tableClass}"><colgroup><col/><col/><col class="money-col"/><col/><col class="money-col"/></colgroup><thead><tr><th>Description</th><th>Qty</th><th>Unit Price</th><th>Taxable</th><th>Total</th></tr></thead><tbody>`;
      let sectionTotal = 0;
      let sectionTaxableTotal = 0;
      items.forEach((item) => {
        if (isSeparatorRow(item)) {
          if (item.showSubtotal && sectionTotal > 0) {
            const hasSectionTax = includeTax && taxRate > 0 && sectionTaxableTotal > 0;
            const sectionTax = hasSectionTax ? sectionTaxableTotal * taxRate / 100 : 0;
            const sectionTotalWithTax = sectionTotal + sectionTax;
            if (hasSectionTax) {
              itemsTableHtml += `<tr class="item-section-subtotal-row"><td colspan="4" class="section-subtotal-label">Section Subtotal</td><td class="money-cell section-subtotal-value">${formatCurrency(sectionTotal)}</td></tr>`;
              itemsTableHtml += `<tr class="item-section-tax-row"><td colspan="4" class="section-subtotal-label">Section Tax (${taxRate}%)</td><td class="money-cell section-subtotal-value">${formatCurrency(sectionTax)}</td></tr>`;
            }
            itemsTableHtml += `<tr class="item-section-subtotal-row"><td colspan="4" class="section-subtotal-label">Section Total</td><td class="money-cell section-subtotal-value">${formatCurrency(sectionTotalWithTax)}</td></tr>`;
          }
          sectionTotal = 0;
          sectionTaxableTotal = 0;
          itemsTableHtml += '<tr class="item-separator-row"><td colspan="5" class="item-separator"><div class="separator-line"></div></td></tr>';
        } else {
          const itemTotal = parseFloat(item.total) || 0;
          sectionTotal += itemTotal;
          if (item.taxable !== false) sectionTaxableTotal += itemTotal;
          itemsTableHtml += `<tr>
            <td>${escapeHtml(String(item.description || ''))}</td>
            <td>${item.quantity ?? 0}</td>
            <td class="money-cell">${formatCurrency(item.unit_price)}</td>
            <td>${item.taxable !== false ? 'Yes' : 'No'}</td>
            <td class="money-cell">${formatCurrency(item.total)}</td>
          </tr>`;
        }
      });
      itemsTableHtml += '</tbody></table>';
      
      // Insert table HTML
      const tempDiv = parser.parseFromString(itemsTableHtml, 'text/html');
      const tableNode = tempDiv.body.firstChild;
      if (tableNode) {
        token.replaceWith(tableNode);
      } else {
        token.remove();
      }
      return;
    }
    
    if (key === 'totals_section') {
      // Generate totals section HTML
      let totalsSectionHtml = '';
      if (!hideTotals) {
        totalsSectionHtml = '<div class="print-totals">';
        totalsSectionHtml += `<div class="print-totals-row"><span>Subtotal:</span><span>${data.invoice?.subtotal ? (typeof data.invoice.subtotal === 'string' && data.invoice.subtotal.startsWith('$') ? data.invoice.subtotal : formatCurrency(data.invoice.subtotal)) : formatCurrency(0)}</span></div>`;
        
        if (parseCurrencyToNumber(data.invoice?.taxable_subtotal) > 0) {
          totalsSectionHtml += `<div class="print-totals-row"><span>Taxable Amount:</span><span>${formatCurrency(data.invoice.taxable_subtotal)}</span></div>`;
        }
        
        if (parseCurrencyToNumber(data.invoice?.non_taxable_subtotal) > 0) {
          totalsSectionHtml += `<div class="print-totals-row"><span>Non-Taxable Amount:</span><span>${formatCurrency(data.invoice.non_taxable_subtotal)}</span></div>`;
        }
        
        let taxAmount = parseCurrencyToNumber(data.invoice?.tax) || parseCurrencyToNumber(data.invoice?.tax_amount);
        const taxableSubtotal = parseCurrencyToNumber(data.invoice?.taxable_subtotal);
        const taxRate = parseFloat(data.invoice?.tax_rate || data.tax_rate || 0);
        if (taxAmount <= 0 && taxableSubtotal > 0 && taxRate > 0) {
          taxAmount = taxableSubtotal * taxRate / 100;
        }
        if (taxAmount > 0) {
          totalsSectionHtml += `<div class="print-totals-row"><span>Tax (${taxRate}%):</span><span>${formatCurrency(taxAmount)}</span></div>`;
        }
        
        totalsSectionHtml += `<div class="print-totals-row print-total"><span>Total:</span><span>${data.invoice?.total ? (typeof data.invoice.total === 'string' && data.invoice.total.startsWith('$') ? data.invoice.total : formatCurrency(data.invoice.total)) : formatCurrency(0)}</span></div>`;
        totalsSectionHtml += '</div>';
      }
      
      // Insert totals HTML
      if (totalsSectionHtml) {
        const tempDiv = parser.parseFromString(totalsSectionHtml, 'text/html');
        const totalsNode = tempDiv.body.firstChild;
        if (totalsNode) {
          token.replaceWith(totalsNode);
        } else {
          token.remove();
        }
      } else {
        token.remove();
      }
      return;
    }
    
    if (key === 'notes_section') {
      // Generate notes section HTML
      const notesContent = data.notes_section || data.invoice?.notes || data.notes || '';
      const notesSectionHtml = notesContent 
        ? `<div class="print-notes"><p><strong>Notes:</strong></p><p>${notesContent}</p></div>` 
        : '';
      
      if (notesSectionHtml) {
        const tempDiv = parser.parseFromString(notesSectionHtml, 'text/html');
        const notesNode = tempDiv.body.firstChild;
        if (notesNode) {
          token.replaceWith(notesNode);
        } else {
          token.remove();
        }
      } else {
        token.remove();
      }
      return;
    }
    
    // Regular token replacement - preserve styling with span; apply formatter from catalog if set
    // Per-row tokens (e.g. invoice.items_table.1.unit_price) use data row index, not raw array index,
    // so row 1 = second line item even when the array has a separator at index 1
    const keyParts = key.split('.');
    const itemsTableMatch = key.match(/^(invoice|estimate)\.(items_table|line_items)\.(\d+)\.(.+)$/);
    let value;
    if (itemsTableMatch) {
      const rowIndex = parseInt(itemsTableMatch[3], 10);
      const subPath = itemsTableMatch[4]; // e.g. "unit_price" or "total"
      const row = dataRows[rowIndex];
      value = row ? (subPath.split('.').reduce((obj, part) => obj?.[part], row)) : undefined;
      // Format currency fields so they display as $X.XX
      if (value !== undefined && value !== null && (subPath === 'unit_price' || subPath === 'total')) {
        const n = typeof value === 'string' ? parseFloat(value.replace(/[$,]/g, '')) : Number(value);
        value = Number.isNaN(n) ? String(value) : `$${n.toFixed(2)}`;
      }
    } else {
      value = keyParts.reduce((obj, part) => {
        if (part.match(/^\d+$/)) {
          return obj ? obj[parseInt(part)] : undefined;
        }
        return obj ? obj[part] : undefined;
      }, data);
    }

    if (value !== undefined && value !== null) {
      const tokenDef = getTokenByKey(key);
      const formatterName = tokenDef?.formatter || null;
      const displayValue = formatterName ? applyFormatter(formatterName, value) : String(value);
      const span = doc.createElement('span');
      span.setAttribute('data-replaced-key', key);
      span.appendChild(doc.createTextNode(displayValue));
      const parent = token.parentElement;
      if (parent && parent !== doc.body && parent.cloneNode) {
        const inlineStyle = parent.getAttribute('style');
        if (inlineStyle) span.setAttribute('style', inlineStyle);
        const cls = parent.getAttribute('class');
        if (cls) span.setAttribute('class', cls);
      }
      token.replaceWith(span);
    } else {
      token.remove();
    }
  });

  // Get HTML after token replacement
  let renderedHtml = doc.body.innerHTML;
  
  // Fallback: Also handle old {{variable}} string format for backward compatibility
  // This catches any {{variable}} that were not converted to merge tokens
  const clientCityStateZip = data.client?.city_state_zip || 
    [data.client?.city, data.client?.state, data.client?.zip].filter(Boolean).join(', ') || '';
  
  renderedHtml = renderedHtml
    // Document type
    .replace(/\{\{document_type\}\}/g, data.invoice?.document_type || data.document_type || '')
    .replace(/\{\{invoice\.document_type\}\}/g, data.invoice?.document_type || '')
    
    // Client information - old format (underscore)
    .replace(/\{\{client_name\}\}/g, data.client?.name || '')
    .replace(/\{\{client_address\}\}/g, data.client?.address || '')
    .replace(/\{\{client_city_state_zip\}\}/g, clientCityStateZip)
    
    // Client information - new format (dot notation)
    .replace(/\{\{client\.name\}\}/g, data.client?.name || '')
    .replace(/\{\{client\.address\}\}/g, data.client?.address || '')
    .replace(/\{\{client\.city_state_zip\}\}/g, clientCityStateZip)
    .replace(/\{\{client\.email\}\}/g, data.client?.email || '')
    .replace(/\{\{client\.phone\}\}/g, data.client?.phone || '')
    
    // Invoice information - old format (underscore)
    .replace(/\{\{invoice_number\}\}/g, data.invoice?.number || data.invoice_number || '')
    .replace(/\{\{date\}\}/g, data.date || data.invoice?.date || '')
    .replace(/\{\{due_date\}\}/g, data.due_date || data.invoice?.due_date || '')
    
    // Invoice information - new format (dot notation)
    .replace(/\{\{invoice\.number\}\}/g, data.invoice?.number || '')
    .replace(/\{\{invoice\.date\}\}/g, data.invoice?.date || '')
    .replace(/\{\{invoice\.due_date\}\}/g, data.invoice?.due_date || '')
    .replace(/\{\{invoice\.subtotal\}\}/g, data.invoice?.subtotal || '$0.00')
    .replace(/\{\{invoice\.tax\}\}/g, data.invoice?.tax || data.invoice?.tax_amount || '$0.00')
    .replace(/\{\{invoice\.total\}\}/g, data.invoice?.total || '$0.00')
    
    // Facility information
    .replace(/\{\{facility\.name\}\}/g, data.facility?.name || '')
    .replace(/\{\{facility\.address\}\}/g, data.facility?.address || '')
    
    // User information
    .replace(/\{\{user_name\}\}/g, data.user?.name || '')
    .replace(/\{\{user\.name\}\}/g, data.user?.name || '');
  
  // Handle special sections for old string-based tokens if they weren't handled by data-key
  if (renderedHtml.includes('{{items_table}}') || renderedHtml.includes('{{invoice.items_table}}') || renderedHtml.includes('{{invoice.line_items}}') || renderedHtml.includes('{{estimate.items_table}}') || renderedHtml.includes('{{estimate.line_items}}')) {
    const dataRowCount = dataRows.length;
    let totalTableRows = 1;
    let prevSectionTotal = 0;
    items.forEach((item) => {
      if (isSeparatorRow(item)) {
        if (item.showSubtotal && prevSectionTotal > 0) totalTableRows++;
        totalTableRows++;
        prevSectionTotal = 0;
      } else {
        totalTableRows++;
        prevSectionTotal += parseFloat(item.total) || 0;
      }
    });
    const needsXCompact = totalTableRows > 15 || dataRowCount > 10;
    const needsCompact = !needsXCompact && (totalTableRows > 8 || dataRowCount > 5);
    const tableClass = needsXCompact ? 'print-items-table print-items-table--xcompact' : needsCompact ? 'print-items-table print-items-table--compact' : 'print-items-table';
    const taxRate = parseFloat(data.invoice?.tax_rate || data.tax_rate || 0);
    const includeTax = data.invoice?.include_tax !== false;
    let itemsTableHtml = `<table class="${tableClass}"><colgroup><col/><col/><col class="money-col"/><col/><col class="money-col"/></colgroup><thead><tr><th>Description</th><th>Qty</th><th>Unit Price</th><th>Taxable</th><th>Total</th></tr></thead><tbody>`;
    let sectionTotal = 0;
    let sectionTaxableTotal = 0;
    items.forEach((item) => {
      if (isSeparatorRow(item)) {
        if (item.showSubtotal && sectionTotal > 0) {
          const hasSectionTax = includeTax && taxRate > 0 && sectionTaxableTotal > 0;
          const sectionTax = hasSectionTax ? sectionTaxableTotal * taxRate / 100 : 0;
          const sectionTotalWithTax = sectionTotal + sectionTax;
          if (hasSectionTax) {
            itemsTableHtml += `<tr class="item-section-subtotal-row"><td colspan="4" class="section-subtotal-label">Section Subtotal</td><td class="money-cell section-subtotal-value">${formatCurrency(sectionTotal)}</td></tr>`;
            itemsTableHtml += `<tr class="item-section-tax-row"><td colspan="4" class="section-subtotal-label">Section Tax (${taxRate}%)</td><td class="money-cell section-subtotal-value">${formatCurrency(sectionTax)}</td></tr>`;
          }
          itemsTableHtml += `<tr class="item-section-subtotal-row"><td colspan="4" class="section-subtotal-label">Section Total</td><td class="money-cell section-subtotal-value">${formatCurrency(sectionTotalWithTax)}</td></tr>`;
        }
        sectionTotal = 0;
        sectionTaxableTotal = 0;
        itemsTableHtml += '<tr class="item-separator-row"><td colspan="5" class="item-separator"><div class="separator-line"></div></td></tr>';
      } else {
        const itemTotal = parseFloat(item.total) || 0;
        sectionTotal += itemTotal;
        if (item.taxable !== false) sectionTaxableTotal += itemTotal;
        itemsTableHtml += `<tr>
          <td>${escapeHtml(String(item.description || ''))}</td>
          <td>${item.quantity ?? 0}</td>
          <td class="money-cell">${formatCurrency(item.unit_price)}</td>
          <td>${item.taxable !== false ? 'Yes' : 'No'}</td>
          <td class="money-cell">${formatCurrency(item.total)}</td>
        </tr>`;
      }
    });
    itemsTableHtml += '</tbody></table>';
    const safeTableHtml = itemsTableHtml.replace(/\$/g, '$$$$');
    renderedHtml = renderedHtml.replace(/\{\{(items_table|invoice\.items_table|invoice\.line_items|estimate\.items_table|estimate\.line_items)\}\}/g, safeTableHtml);
  }
  
  if (renderedHtml.includes('{{totals_section}}')) {
    let totalsSectionHtml = '';
    if (!hideTotals) {
      totalsSectionHtml = '<div class="print-totals">';
      totalsSectionHtml += `<div class="print-totals-row"><span>Subtotal:</span><span>${data.invoice?.subtotal ? (typeof data.invoice.subtotal === 'string' && data.invoice.subtotal.startsWith('$') ? data.invoice.subtotal : formatCurrency(data.invoice.subtotal)) : formatCurrency(0)}</span></div>`;
      
      if (parseCurrencyToNumber(data.invoice?.taxable_subtotal) > 0) {
        totalsSectionHtml += `<div class="print-totals-row"><span>Taxable Amount:</span><span>${formatCurrency(data.invoice.taxable_subtotal)}</span></div>`;
      }
      
      if (parseCurrencyToNumber(data.invoice?.non_taxable_subtotal) > 0) {
        totalsSectionHtml += `<div class="print-totals-row"><span>Non-Taxable Amount:</span><span>${formatCurrency(data.invoice.non_taxable_subtotal)}</span></div>`;
      }
      
      let taxAmount = parseCurrencyToNumber(data.invoice?.tax) || parseCurrencyToNumber(data.invoice?.tax_amount);
      const taxableSubtotal = parseCurrencyToNumber(data.invoice?.taxable_subtotal);
      const taxRate = parseFloat(data.invoice?.tax_rate || data.tax_rate || 0);
      if (taxAmount <= 0 && taxableSubtotal > 0 && taxRate > 0) {
        taxAmount = taxableSubtotal * taxRate / 100;
      }
      if (taxAmount > 0) {
        totalsSectionHtml += `<div class="print-totals-row"><span>Tax (${taxRate}%):</span><span>${formatCurrency(taxAmount)}</span></div>`;
      }
      
      totalsSectionHtml += `<div class="print-totals-row print-total"><span>Total:</span><span>${data.invoice?.total ? (typeof data.invoice.total === 'string' && data.invoice.total.startsWith('$') ? data.invoice.total : formatCurrency(data.invoice.total)) : formatCurrency(0)}</span></div>`;
      totalsSectionHtml += '</div>';
    }
    renderedHtml = renderedHtml.replace(/\{\{totals_section\}\}/g, totalsSectionHtml);
  }
  
  if (renderedHtml.includes('{{notes_section}}')) {
    const notesContent = data.notes_section || data.invoice?.notes || data.notes || '';
    const notesSectionHtml = notesContent 
      ? `<div class="print-notes"><p><strong>Notes:</strong></p><p>${notesContent}</p></div>` 
      : '';
    renderedHtml = renderedHtml.replace(/\{\{notes_section\}\}/g, notesSectionHtml);
  }

  // Fallback: inject totals section when template lacks it (ensures overall total shows in print/PDF)
  if (!hideTotals && !renderedHtml.includes('print-totals')) {
    let totalsSectionHtml = '<div class="print-totals">';
    totalsSectionHtml += `<div class="print-totals-row"><span>Subtotal:</span><span>${data.invoice?.subtotal ? (typeof data.invoice.subtotal === 'string' && data.invoice.subtotal.startsWith('$') ? data.invoice.subtotal : formatCurrency(data.invoice.subtotal)) : formatCurrency(0)}</span></div>`;
    if (parseCurrencyToNumber(data.invoice?.taxable_subtotal) > 0) {
      totalsSectionHtml += `<div class="print-totals-row"><span>Taxable Amount:</span><span>${formatCurrency(data.invoice.taxable_subtotal)}</span></div>`;
    }
    if (parseCurrencyToNumber(data.invoice?.non_taxable_subtotal) > 0) {
      totalsSectionHtml += `<div class="print-totals-row"><span>Non-Taxable Amount:</span><span>${formatCurrency(data.invoice.non_taxable_subtotal)}</span></div>`;
    }
    let taxAmount = parseCurrencyToNumber(data.invoice?.tax) || parseCurrencyToNumber(data.invoice?.tax_amount);
    const taxableSubtotal = parseCurrencyToNumber(data.invoice?.taxable_subtotal);
    const taxRate = parseFloat(data.invoice?.tax_rate || data.tax_rate || 0);
    if (taxAmount <= 0 && taxableSubtotal > 0 && taxRate > 0) {
      taxAmount = taxableSubtotal * taxRate / 100;
    }
    if (taxAmount > 0) {
      totalsSectionHtml += `<div class="print-totals-row"><span>Tax (${taxRate}%):</span><span>${formatCurrency(taxAmount)}</span></div>`;
    }
    totalsSectionHtml += `<div class="print-totals-row print-total"><span>Total:</span><span>${data.invoice?.total ? (typeof data.invoice.total === 'string' && data.invoice.total.startsWith('$') ? data.invoice.total : formatCurrency(data.invoice.total)) : formatCurrency(0)}</span></div>`;
    totalsSectionHtml += '</div>';
    // Insert before notes if present, otherwise append
    if (renderedHtml.includes('print-notes')) {
      renderedHtml = renderedHtml.replace(/<div class="print-notes"/, totalsSectionHtml + '<div class="print-notes"');
    } else {
      renderedHtml = renderedHtml + totalsSectionHtml;
    }
  }

  // Clean up per-row token output only: e.g. "invoice.items_table.0" or "estimate.items_table.1" before a $ amount,
  // so the amount displays as "$1,000.00" not "invoice.items_table.0$1,000.00". Do NOT match "items_table" inside
  // our table class "print-items-table" (would corrupt currency in cells). So we only remove the dotted prefix.
  renderedHtml = renderedHtml.replace(/(invoice|estimate)\.items_table\.\d+(?=\$)/g, '$1.');
  renderedHtml = renderedHtml.replace(/(invoice|estimate)\.items_table\.\d+(?=[<\s])/g, '');

  // Ensure totals section appears right after the items table (before terms/conditions)
  if (!hideTotals) {
    const reorderDoc = new DOMParser().parseFromString(renderedHtml, 'text/html');
    const itemsTable = reorderDoc.querySelector('.print-items-table');
    const totalsDiv = reorderDoc.querySelector('.print-totals');
    if (itemsTable && totalsDiv && itemsTable.parentNode) {
      const tableNext = itemsTable.nextSibling;
      if (totalsDiv !== tableNext) {
        itemsTable.parentNode.insertBefore(totalsDiv, tableNext);
      }
    }
    renderedHtml = reorderDoc.body.innerHTML;
  }

  // Sanitize output before preview/PDF/email to prevent XSS
  return sanitizeHtml(renderedHtml);
}

/**
 * Helper to get nested value from object using dot notation
 * @param {object} obj - Source object
 * @param {string} path - Dot notation path (e.g., "client.name")
 * @returns {any} - Value at path or undefined
 */
export function getNestedValue(obj, path) {
  return path.split('.').reduce((current, part) => {
    if (part.match(/^\d+$/)) {
      return current ? current[parseInt(part)] : undefined;
    }
    return current ? current[part] : undefined;
  }, obj);
}
